import React, { useState } from 'react';
import { useLocalize } from 'localize-react';
import { IUnresolvedAlert, UnresolvedAlertType } from '@transferz/fe-models';
import Typography from '@transferz/fe-component-library/src/components/typography/Typography';
import { Button } from '@transferz/fe-component-library/src/components/button/Button';
import CheckIcon from '@transferz/fe-component-library/src/components/icons/CheckIcon';
import PenIcon from '@transferz/fe-component-library/src/components/icons/PenIcon';
import PhoneIcon from '@transferz/fe-component-library/src/components/icons/PhoneIcon';
import { ArrowIcon } from '@transferz/fe-component-library/src/components/icons';
import Notification from '@transferz/fe-component-library/src/components/notification/Notification';
import BottomDrawer from '@transferz/fe-component-library/src/components/bottomDrawer/BottomDrawer';
import PhoneInput from '@transferz/fe-component-library/src/components/phoneInput/PhoneInput';
import { NotificationVariants } from '@transferz/fe-component-library/src/types/notification';
import Input from '@transferz/fe-component-library/src/components/input/Input';
import { useQueryFlightInfo } from '../../../queries/transitTracking/hooks/useQueryFlightInfo';
import { useMutationInitiatePhoneVerification } from '../../../queries/booking/hooks/useMutationInitiatePhoneVerification';
import { useMutationProcessJourneyChanges } from '../../../queries/booking/hooks/useMutationProcessJourneyChanges';
import { useMutationResolveAlertWithChangeId } from '../../../queries/booking/hooks/useMutationResolveAlertWithChangeId';
import { useLocalStorage } from '../../../hooks/useLocalStorage';
import './unresolvedAlerts.scss';

interface IUnresolvedAlertsProps {
  unresolvedAlerts: IUnresolvedAlert[];
  showAlertResolveNotification: boolean;
  alertResolveNotificationMessage: string;
  alertResolvenotificationVariant: string;
  refetchUnresolvedAlerts: Function;
}
export const UnresolvedAlerts: React.FC<IUnresolvedAlertsProps> = ({
  unresolvedAlerts,
  showAlertResolveNotification = false,
  alertResolveNotificationMessage = '',
  alertResolvenotificationVariant = '',
  refetchUnresolvedAlerts,
}) => {
  const { translate } = useLocalize();
  const [isPopupOpen, setIsPopupOpen] = useState(false);
  const [alert, setAlert] = useState<any>({});
  const [phoneNumber, setPhoneNumber] = useState<string>('');
  const [initAlertResolutionNotification, setValue, removeValue] = useLocalStorage('initAlertResolutionNotification');
  const [notificationVisible, setNotificationVisible] = useState(showAlertResolveNotification);
  const [notificationVariant, setNotificationVariant] = useState(alertResolvenotificationVariant);
  const [notificationMessage, setNotificationMessage] = useState(alertResolveNotificationMessage);
  const [isFlightNumberCorrect, setIsFlightNumberCorrect] = useState(false);
  const [flightNumber, setFlightNumber] = useState('');
  const [flightNumberVerifyLoading, setFlightNumberVerifyLoading] = useState(false);
  const [flightNumberError, setFlightNumberError] = useState('');
  const [showInitAlertResolutionNotification, setShowInitAlertResolutionNotification] = useState(initAlertResolutionNotification || [] as any);
  const [initPhoneVerification] = useMutationInitiatePhoneVerification();
  const [processJourneyChanges, { loading: processJourneyChangesLoading }] = useMutationProcessJourneyChanges();
  const [resolveAlertWithChangeId, { loading: resolveAlertWithChangeIdLoading }] = useMutationResolveAlertWithChangeId();
  const { refetch: getFlightInfo, loading: flightInfoLoading } = useQueryFlightInfo({
    variables: {
      params: {
        iataFlightNumber: null,
        arrivalDate: null,
      },
      skip: true,
    },
  });
  const isJourneyPage = window.location.pathname.includes('journey');
  const handlePopupClose = () => {
    setIsPopupOpen(false);
    setFlightNumberError('');
    setIsFlightNumberCorrect(false);
    setFlightNumber('');
  };
  const handleInitPhoneVerification = (id: number, phone: string) => {
    let phoneNumber: string = phone;
    if ((/\s/).test(phoneNumber)) {
      phoneNumber = phone.replace(' ', '');
    }
    initPhoneVerification({
      variables: {
        id,
        phoneNumber,
      },
    })
      .then(() => {
        const currentAlertResolutionNotification = [{
          id,
          phoneNumber,
          resolveinitiated: true,
        }];
        const initAlertResolutionNotification = showInitAlertResolutionNotification?.length
          ? showInitAlertResolutionNotification.map((notification: any, index: number) => {
            return { ...notification, ...currentAlertResolutionNotification[index] };
          })
          : currentAlertResolutionNotification;
        setShowInitAlertResolutionNotification(initAlertResolutionNotification);
        setValue('initAlertResolutionNotification', initAlertResolutionNotification);
        setIsPopupOpen(false);
      })
      .catch((e: any) => {
        setNotificationVariant(NotificationVariants.error);
        setNotificationMessage(translate('booking_somethingWentWrong'));
        setNotificationVisible(true);
      });
  };
  let flightInfoTimeout: any;
  const handleVerifyFlightNumber = (flightNumber: string, alert: any) => {
    if (flightNumber.length > 2) {
      setFlightNumberVerifyLoading(true);
      clearTimeout(flightInfoTimeout);
      const flightInfoParams: any = {
        iataFlightNumber: flightNumber,
      };
      if (alert?.isJourneyInbound) {
        flightInfoParams.arrivalDate = alert?.pickupDate.split('T')[0];
        flightInfoParams.arrivalAirportIataCode = alert?.hubIataCode;
      } else {
        flightInfoParams.departureDate = alert?.pickupDate.split('T')[0];
        flightInfoParams.departureAirportIataCode = alert?.hubIataCode;
      }
      flightInfoTimeout = setTimeout(() => {
        getFlightInfo({
          params: flightInfoParams,
          skip: false,
        })
          .then((response: any) => {
            if (response?.data?.flightInfo?.length) {
              setIsFlightNumberCorrect(true);
              setFlightNumber(flightNumber);
              setFlightNumberError('');
            } else {
              setIsFlightNumberCorrect(false);
              setFlightNumberError(translate('n_enteredFlightIncorrect'));
            }
          })
          .catch(() => {
            setFlightNumberError(translate('n_enteredFlightIncorrect'));
          })
          .finally(() => {
            setFlightNumberVerifyLoading(false);
          });
      }, 300);
    } else {
      setFlightNumberError(translate('n_enteredFlightIncorrect'));
    }
  };
  const handleSaveFlightNumber = async (alert: any) => {
    const processJourneyChangesResponse = await processJourneyChanges({
      variables: {
        params: {
          journeyId: alert?.journeyId,
          travellerInfo: {
            ...alert?.travellerInfo,
            __typename: undefined,
            flightNumber,
          },
        },
      },
    });
    const processJourneyChangesData = processJourneyChangesResponse?.data?.processJourneyChanges;
    if (processJourneyChangesData?.id) {
      resolveAlertWithChangeId({
        variables: {
          alertId: alert?.id,
          journeyChangeIds: [processJourneyChangesData?.id],
        },
      })
        .then(() => {
          setNotificationVariant(NotificationVariants.success);
          setNotificationMessage(translate('h_successfully_verified_flight_number'));
          refetchUnresolvedAlerts();
        })
        .catch(() => {
          setNotificationVariant(NotificationVariants.error);
          setNotificationMessage(translate('booking_somethingWentWrong'));
        })
        .finally(() => {
          setNotificationVisible(true);
          setIsPopupOpen(false);
        });
    }
  };

  return (
    <>
      {notificationVisible
        && (<div className={`custom-notification-container ${notificationVariant === NotificationVariants.error ? 'custom-error' : 'custom-success'}`}>
          <Notification
            variant={notificationVariant as any}
            message={notificationMessage}
            isSnackBar={true}
            icon={(notificationVariant === NotificationVariants.success) && <CheckIcon width={24} color="atlantis5" /> as any}
          />
        </div>)
      }
      {unresolvedAlerts?.length
        ? (
          <div className={`unresolved-alerts ${isJourneyPage ? 'journey-alerts' : 'booking-alerts'} ${unresolvedAlerts?.length > 1 ? 'alert-space' : ''}`}>
            {unresolvedAlerts?.map((alert: IUnresolvedAlert, index: number) => {
              const alertResolveInitiated = showInitAlertResolutionNotification && showInitAlertResolutionNotification.find((resolution: any) => resolution.id === alert.id);
              return (
                alertResolveInitiated?.resolveinitiated
                  ? (
                    <div className="custom-notification neutral" key={index}>
                      <Notification
                        className="notification-stroke-neutral"
                        customTitle={(
                          <div className="custom-title-container">
                            <PhoneIcon
                              color="blue5"
                              width={24}
                              className="notification-icon"
                            />
                            <Typography variant="h5" color="blue4">{`${translate('h_verifications_SMS_sent')} (${alertResolveInitiated?.phoneNumber})`}</Typography>
                          </div>
                        )}
                        variant={NotificationVariants.neutral}
                        message={''}
                        custom={true}
                      >
                        <div className="notification-content">
                          <Typography variant="subtitle1" color="dark">
                            {translate('c_didnot_receive_SMS')}
                          </Typography>
                          <a href="" className="custom-link" onClick={(e) => {
                            e.preventDefault();
                            setIsPopupOpen(true);
                            setAlert(alert);
                          }}>
                            {translate('c_adjust_phone_number')}
                          </a>
                        </div>
                      </Notification>
                    </div>
                  )
                  : (
                    <div className={`custom-notification notification-${alert.type}`} key={index}>
                      <Notification
                        className="notification-stoke-danger"
                        customTitle={<Typography variant="h5" color="red5">{translate('h_fix_issue')}</Typography>}
                        variant={NotificationVariants.error}
                        message={''}
                        custom={true}
                      >
                        <div className="notification-content">
                          <Typography variant="subtitle1" color="dark">
                            {alert.type === UnresolvedAlertType.IncorrectTravellerPhone
                              ? translate('c_incorrect_phone_number')
                              : alert.type === UnresolvedAlertType.FlightNumberNotFound
                                ? translate(isJourneyPage ? 'c_flight_number_not_found' : 'c_flightNumber_notFound_forJourney', { journeyCode: alert.journeyCode as string })
                                : alert.type === UnresolvedAlertType.FlightNumberNotProvided
                                  ? translate(isJourneyPage ? 'c_flight_number_not_provided' : 'c_flightNumber_notProvided_forJourney', { journeyCode: alert.journeyCode as string })
                                  : translate(isJourneyPage ? 'c_flight_number_incorrect_airport' : 'c_flightNumber_incorrectAirport_forJourney', { journeyCode: alert.journeyCode as string })
                            }
                            {alert.type === UnresolvedAlertType.IncorrectTravellerPhone
                              ? <i>&nbsp;{alert?.phone}</i>
                              : alert.type === UnresolvedAlertType.FlightNumberNotProvided
                                ? null
                                : <i>&nbsp;{alert?.flightNumber}</i>
                            }
                          </Typography>
                          <div className="notification-actions">
                            {alert.type === UnresolvedAlertType.IncorrectTravellerPhone
                              ? (<Button
                                className="accept"
                                textColor="light"
                                variant="confirm"
                                priority="primary"
                                icon={<CheckIcon width={15} color="light" />}
                                label={translate('b_this_is_correct')}
                                onClick={() => handleInitPhoneVerification(alert?.id, alert?.phone as any)}
                                data-button-id="accept"
                              />
                              )
                              : null
                            }
                            <Button
                              className="resolve"
                              textColor="light"
                              variant="cancel"
                              priority="primary"
                              icon={<PenIcon width={15} color="light" />}
                              label={translate('b_add_correct_info')}
                              onClick={() => {
                                setIsPopupOpen(true);
                                setAlert(alert);
                              }}
                              data-button-id="resolve"
                            />
                          </div>
                        </div>
                      </Notification>
                    </div>
                  )
              );
            })}
          </div>
        )
        : ''
      }
      {isPopupOpen && (<BottomDrawer
        className="phone-input-popup"
        title={translate(alert?.type === UnresolvedAlertType.IncorrectTravellerPhone ? 'h_add_correct_phone_number' : 'h_add_correct_flight_number')}
        isOpen={isPopupOpen}
        onClose={() => handlePopupClose()}
        enableDesktopView={true}
      >
        {alert?.type === UnresolvedAlertType.IncorrectTravellerPhone
          ? (
            <PhoneInput
              fillContent={true}
              className="custom-phone-input"
              path={`${window.location.origin}/flags/4x3`}
              label={translate('label_your_mobile_number')}
              initialValue=""
              onChange={(phoneNumber: string) => setPhoneNumber(phoneNumber)}
            />)
          : <Input
              label={translate('label_flight_number')}
              className="custom-flight-number-input"
              confirmed={isFlightNumberCorrect}
              loading={flightInfoLoading || flightNumberVerifyLoading}
              error={flightNumberError}
              onChange={(event: any) => handleVerifyFlightNumber(event?.target?.value, alert)}
          />
        }
        <div className="phone-input-popup-footer d-flex justify-content-flex-end">
          <Button
            className="mr-16 close-btn"
            label={translate('close')}
            priority="secondary"
            onClick={() => handlePopupClose()}
            data-button-id="close-phone-popup"
          />
          {alert?.type === UnresolvedAlertType.IncorrectTravellerPhone
            ? (<Button
              label={translate('b_send_verification_SMS')}
              priority="primary"
              className="verification-btn"
              variant="info"
              iconPosition="right"
              icon={<ArrowIcon direction="right" color="light"/>}
              data-button-id="verify-phone-number"
              onClick={() => handleInitPhoneVerification(alert?.id, phoneNumber)}
            />)
            : (<Button
              label={translate('b_flight_alert_save_continue')}
              priority="primary"
              className="verification-btn"
              variant="info"
              iconPosition="right"
              disabled={!isFlightNumberCorrect || !!flightNumberError || flightNumberVerifyLoading || flightInfoLoading}
              icon={<ArrowIcon direction="right" color="light"/>}
              data-button-id="verify-phone-number"
              loading={processJourneyChangesLoading || resolveAlertWithChangeIdLoading}
              onClick={() => handleSaveFlightNumber(alert)}
            />
            )
          }
        </div>
      </BottomDrawer>
      )}
    </>
  );
};
